import { MoreHorizontal } from 'lucide-react';
import * as React from 'react';

import MaterialIcon from '@/components/atom/MaterialIcon';
import theme from '@/styles/theme';
import { cn } from '@/utils/style';
import { VariantProps, cva } from 'class-variance-authority';
import Link from 'next/link';

const paginationVariants = cva('typography-ui-17sb flex items-center justify-center', {
  variants: {
    variant: {
      disabled: 'rounded-[50%] bg-white',
      default: 'rounded-[50%] bg-white text-gray-500 hover:bg-gray-50',
      active: 'rounded-[50%] bg-primary-600 text-white',
    },
    size: {
      default: 'h-[32px] w-[32px]',
      icon: 'h-[28px] w-[28px]',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
});

const Pagination = ({ className, ...props }: React.ComponentProps<'nav'>) => (
  <nav
    role='navigation'
    aria-label='pagination'
    className={cn('mx-auto mt-[20px] flex w-full justify-center', className)}
    {...props}
  />
);
Pagination.displayName = 'Pagination';

const PaginationContent = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(
  ({ className, ...props }, ref) => (
    <ul ref={ref} className={cn('flex flex-row items-center gap-[12px]', className)} {...props} />
  )
);
PaginationContent.displayName = 'PaginationContent';

const PaginationItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn('', className)} {...props} />
));
PaginationItem.displayName = 'PaginationItem';

type PaginationLinkProps = {
  isActive?: boolean;
  scroll?: boolean;
} & VariantProps<typeof paginationVariants> &
  React.ComponentProps<typeof Link>;

const PaginationLink = ({
  className,
  isActive,
  variant = 'default',
  size = 'default',
  href,
  scroll = true,
  ...props
}: PaginationLinkProps) => {
  const disabled = variant === 'disabled';
  return (
    <Link aria-current={isActive ? 'page' : undefined} href={href} scroll={scroll} {...props}>
      <a
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : undefined}
        className={cn(
          paginationVariants({
            variant,
            size,
          }),
          className,
          disabled ? 'pointer-events-none' : ''
        )}
      >
        {props.children}
      </a>
    </Link>
  );
};

PaginationLink.displayName = 'PaginationLink';

const PaginationPrevious = ({ className, ...props }: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label='Go to previous page'
    size='icon'
    variant={props.isActive ? 'default' : 'disabled'}
    className={cn('', className)}
    {...props}
  >
    <MaterialIcon
      name={'keyboard_arrow_left'}
      size={28}
      color={props.isActive ? theme.color.gray600 : theme.color.gray200}
      filled
    />
  </PaginationLink>
);
PaginationPrevious.displayName = 'PaginationPrevious';

const PaginationDoublePrevious = ({ className, ...props }: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label='Go to double previous page'
    size='icon'
    variant={props.isActive ? 'default' : 'disabled'}
    className={cn('', className)}
    {...props}
  >
    <MaterialIcon
      name={'keyboard_double_arrow_left'}
      size={28}
      color={props.isActive ? theme.color.gray600 : theme.color.gray200}
      filled
    />
  </PaginationLink>
);
PaginationDoublePrevious.displayName = 'PaginationDoublePrevious';

const PaginationNext = ({ className, ...props }: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label='Go to next page'
    size='icon'
    variant={props.isActive ? 'default' : 'disabled'}
    className={cn('', className)}
    {...props}
  >
    <MaterialIcon
      name={'keyboard_arrow_right'}
      size={28}
      color={props.isActive ? theme.color.gray600 : theme.color.gray200}
      filled
    />
  </PaginationLink>
);
PaginationNext.displayName = 'PaginationNext';

const PaginationDoubleNext = ({ className, ...props }: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label='Go to double next page'
    size='icon'
    variant={props.isActive ? 'default' : 'disabled'}
    className={cn('', className)}
    {...props}
  >
    <MaterialIcon
      name={'keyboard_double_arrow_right'}
      size={28}
      color={props.isActive ? theme.color.gray600 : theme.color.gray200}
      filled
    />
  </PaginationLink>
);
PaginationDoubleNext.displayName = 'PaginationDoubleNext';

const PaginationEllipsis = ({ className, ...props }: React.ComponentProps<'span'>) => (
  <span aria-hidden className={cn('flex h-9 w-9 items-center justify-center', className)} {...props}>
    <MoreHorizontal className='h-4 w-4' />
    <span className='sr-only'>More pages</span>
  </span>
);
PaginationEllipsis.displayName = 'PaginationEllipsis';

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationDoublePrevious,
  PaginationDoubleNext,
};
